import { memo, useRef, useState } from "react";

import { formatTimeMs } from "~/lib/format-time";
import { useDragOnPointerDown } from "~/lib/hooks/use-drag-on-pointer-down";
import { css, cva } from "~/styled-system/css";

import { FRAME_INCREMENT_SIZE_25_FPS, TrimmableRegion, TrimmableRegion_Id } from "./audio-trimmer";

const MIN_MOVEMENT_SCRUB_THRESHOLD = 2;
const TIMESTAMP_OUTSIDE_PLACEMENT_THRESHOLD = 1500;

interface AudioTrimmerRegionProps {
  region: TrimmableRegion;
  regionCount: number;
  audioDuration: number;
  trimMode: "duration" | "regions" | "none";
  handleRegionResize: (
    regionId: TrimmableRegion_Id,
    delta: { x: number; y: number },
    side: "left" | "right" | "center"
  ) => void;
  handleJumpToProgress: (event: React.PointerEvent<HTMLDivElement>, prepareToDrag: boolean) => void;
  handleCommitRegion: () => void;
  onSelect: (id: TrimmableRegion_Id) => void;
  active: boolean;
  height: string;
  disabled?: boolean;
  onStartMoving?: (side: "left" | "right" | "center") => void;
  onDoneMoving?: (side: "left" | "right" | "center") => void;
  showDuration?: boolean;
  delayBeforeHideDuration?: number;
}

export const AudioTrimmerRegion = memo(
  ({
    region,
    audioDuration,
    trimMode,
    handleRegionResize,
    handleJumpToProgress,
    handleCommitRegion,
    active,
    onSelect,
    height,
    disabled,
    onStartMoving,
    onDoneMoving,
    showDuration,
    delayBeforeHideDuration = 1000,
  }: AudioTrimmerRegionProps) => {
    const [isMoving, setIsMoving] = useState(false);
    const movingTimeout = useRef<NodeJS.Timeout>();
    const handleLeftGrip = useDragOnPointerDown<{ regionId: TrimmableRegion_Id }>({
      onStart: (_e) => {
        onSelect(region.id);
        onStartMoving?.("left");
        return {
          shouldStart: true,
          context: { regionId: region.id },
        };
      },
      onMove: ({ delta }) => {
        if (disabled) {
          return;
        }
        setIsMoving(true);
        if (movingTimeout.current) {
          clearTimeout(movingTimeout.current);
        }
        handleRegionResize(region.id, delta, "left");
      },
      onDone: () => {
        onDoneMoving?.("left");
        handleCommitRegion();
        movingTimeout.current = setTimeout(() => setIsMoving(false), delayBeforeHideDuration);
      },
    });

    const handleRightGrip = useDragOnPointerDown<{ regionId: TrimmableRegion_Id }>({
      onStart: (_e) => {
        onStartMoving?.("right");
        return {
          shouldStart: true,
          context: { regionId: region.id },
        };
      },
      onMove: ({ delta }) => {
        if (disabled) {
          return;
        }
        setIsMoving(true);
        if (movingTimeout.current) {
          clearTimeout(movingTimeout.current);
        }
        handleRegionResize(region.id, delta, "right");
      },
      onDone: () => {
        onDoneMoving?.("right");
        handleCommitRegion();
        movingTimeout.current = setTimeout(() => setIsMoving(false), delayBeforeHideDuration);
      },
    });

    const handleMiddleGrip = useDragOnPointerDown<{ regionId: TrimmableRegion_Id }>({
      onStart: (_e) => {
        onStartMoving?.("center");
        return {
          shouldStart: true,
          context: { regionId: region.id },
        };
      },
      onMove: ({ delta }) => {
        if (disabled) {
          return;
        }
        handleRegionResize(region.id, delta, "center");
      },
      onDone: ({ event, delta }) => {
        onDoneMoving?.("center");
        const wasClickNotDrag = Math.abs(delta.x) <= MIN_MOVEMENT_SCRUB_THRESHOLD;
        // handle seeking if user didn't drag on center item
        if (wasClickNotDrag && event) {
          handleJumpToProgress(event as unknown as React.PointerEvent<HTMLDivElement>, false);
        }
        handleCommitRegion();
      },
    });

    const regionDuration = region.endMs - region.startMs;
    const adjustedTotalDuration =
      Math.round(audioDuration / FRAME_INCREMENT_SIZE_25_FPS) * FRAME_INCREMENT_SIZE_25_FPS;

    const movable = regionDuration < adjustedTotalDuration;

    if (trimMode === "none") {
      return null;
    }

    return (
      <div
        className={audioTrimmerRegionStyles({ trimMode, disabled, movable })}
        {...(trimMode === "duration" ? { onPointerDown: handleMiddleGrip } : null)}
        style={{
          left: `${(region.startMs / audioDuration) * 100}%`,
          width: `${((region.endMs - region.startMs) / audioDuration) * 100}%`,
        }}
      >
        {/* don't show the handles if it's region trim mode + first region in the array */}
        {(trimMode !== "regions" || region.index !== 0) && (
          <div className={regionDragHandleContainerStyles}>
            <div
              className={css(regionDragHandleStyles.raw({ trimMode, active }), { height })}
              onPointerDown={handleLeftGrip}
              role="button"
            />
            <div
              className={css(regionDragHandleStyles.raw({ trimMode, active }), { height })}
              onPointerDown={handleRightGrip}
              role="button"
            />
          </div>
        )}
        <div className={regionOverlayStyles}>
          <div className={regionOverlayMixBlenderStyles} />
          {showDuration && (
            <div
              className={durationIndicatorStyles({
                isMoving,
                placement:
                  regionDuration < TIMESTAMP_OUTSIDE_PLACEMENT_THRESHOLD ? "outside" : "inside",
              })}
            >
              <span>{formatTimeMs(regionDuration)}s</span>
            </div>
          )}
        </div>
      </div>
    );
  }
);
AudioTrimmerRegion.displayName = "AudioTrimmerRegion";

const audioTrimmerRegionStyles = cva({
  base: {
    position: "absolute",
    top: 0,
    height: "100%",
    border: "solid 1px token(colors.$sunny)",
    borderLeftWidth: 0,
    borderRightWidth: 0,

    "&:first-child": {
      borderTopLeftRadius: "8px",
      borderBottomLeftRadius: "8px",
      borderLeftWidth: 1,
    },
    "&:last-child": {
      borderTopRightRadius: "8px",
      borderBottomRightRadius: "8px",
      borderRightWidth: 1,
    },
  },
  variants: {
    trimMode: {
      regions: {},
      duration: {},
      none: {},
    },
    disabled: {
      true: {},
      false: {},
    },
    movable: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      movable: true,
      trimMode: "duration",
      css: {
        cursor: "move",
      },
    },
    {
      movable: true,
      trimMode: "duration",
      disabled: true,
      css: {
        cursor: "not-allowed",
        pointerEvents: "none",
      },
    },
  ],
});

const regionDragHandleContainerStyles = css({
  position: "relative",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const regionDragHandleStyles = cva({
  base: {
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    top: "-1px",
    width: "18px",
    zIndex: "$centerFixedBox",
    cursor: "ew-resize",
    "&::before": {
      display: "block",
      content: '" "',
      transition: "scale 150ms ease",
    },
    "&:hover": {
      "&::before": {
        scale: 1,
      },
    },
  },
  variants: {
    active: { true: {} },
    trimMode: {
      duration: {
        "&:first-child": {
          left: "-2px",
        },
        "&:last-child": {
          right: "-2px",
        },
        "&::before": {
          backgroundColor: "$sunny",
          width: "2px",
          height: "10px",
          borderRadius: "8px",
        },
        "&:hover": {
          "&::before": {
            backgroundColor: "#fff",
            scale: 1.3,
          },
        },
      },
      regions: {
        left: "-9px",
        "&:last-child": {
          display: "none",
        },
        // white grab handle
        "&::before": {
          content: '" "',
          position: "absolute",
          opacity: 0,
          scale: 0.1,
          display: "block",
          borderRadius: "30px",
          backgroundColor: "#fff",
          width: "6px",
          height: "24px",
          zIndex: "$centerFixedBox",
        },
        // yellow vertical line serving as border between regions
        "&::after": {
          content: '" "',
          position: "absolute",
          display: "block",
          backgroundColor: "$sunny",
          width: "1px",
          height: "100%",
        },
        "&:hover": {
          "&::before": {
            opacity: 1,
          },
        },
      },
      none: {},
    },
  },
  compoundVariants: [
    {
      trimMode: "regions",
      active: true,
      css: {
        "&::before": {
          opacity: 1,
          scale: 1,
        },
        "&:after": {
          backgroundColor: "$gray.100",
        },
        "&:hover": {
          "&::before": {
            scale: 1.1,
          },
        },
      },
    },
  ],
});

const regionOverlayStyles = css({
  pointerEvents: "auto",
  position: "relative",
  height: "100%",
  width: "100%",
});

const regionOverlayMixBlenderStyles = css({
  mixBlendMode: "overlay",
  backgroundColor: "$sunny",
  height: "100%",
  width: "100%",
  position: "absolute",
  inset: 0,
  borderRadius: "4px",
});

const durationIndicatorStyles = cva({
  base: {
    position: "absolute",

    color: "white",
    textStyle: "body-4-subtle",
    padding: "2px",
    borderRadius: "4px",
    backgroundColor: "$surface.overlay",
    minWidth: "20px",
    fontVariant: "tabular-nums",
    zIndex: "$overContent",
    transition: "opacity 100ms ease",
  },
  variants: {
    isMoving: {
      true: {
        opacity: 1,
      },
      false: {
        opacity: 0,
      },
    },
    placement: {
      inside: {
        right: "15px",
        bottom: "5px",
      },
      outside: {
        right: "-50px",
        bottom: "5px",
      },
    },
  },
});
